<template>
  <HeadTitle
    title="Grupos de documentos"
    description=" En esta sección podrás ver los grupos de documentos de las nóminas"
  />
  <Toolset
    ref="toolset"
    :searcher="searcher"
    add-button-icon="pi pi-plus"
    add-button-label="Crear nuevo grupo de documentos"
    :to-add="toAdd"
    class="mt-5 text-sm font-light"
  />
  <br />
  <DataTable
    class="tabla mb-5"
    :value="documentGroups.data"
    paginator
    :rows="pagination.limit"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :totalRecords="documentGroups.totalItems"
    :currentPageReportTemplate="currentPageReportTemplate"
    scrollable
    scrollHeight="600px"
  >
    <template #empty> No se encontraron grupos de documentos. </template>
    <Column field="description" header="Descripción">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column field="onlyPayrolls" sortable header="Cant. Nóminas">
      <template #body="{ data, field }">
        {{ data[field].length }}
      </template>
    </Column>
    <Column field="documents" sortable header="Cant. archivos">
      <template #body="{ data, field }">
        {{ data[field].length }}
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización">
      <template #body="{ data, field }">
        <span v-local-date="data[field]"></span>
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <ConfirmPopup></ConfirmPopup>
        <div class="actions">
          <Button
            v-tooltip.bottom="'Ver detalles'"
            class="action-btn view p-button-sm mr-2"
            icon="pi pi-eye"
            @click="goToDetails(data['id'])"
          />
          <Button
            class="action-btn view p-button-sm"
            icon="pi pi-trash"
            @click="deleteApprovalFlow($event, data['id'])"
            severity="danger"
            v-tooltip.bottom="'Eliminar'"
          />
        </div>
      </template>
    </Column>
  </DataTable>
  <Footer>
    <template #left>
      <AuditButton
        :root-id="payrollConfigId"
        only-deleted
        label="Ver registros eliminados"
        :entities="['DocumentGroup']"
      />
    </template>
    <Button
      label="Volver"
      severity="secondary"
      @click="router.push('/payrollsConfig/')"
      icon="pi pi-angle-left"
      class="p-button-text text-sm font-light text-primary"
    />
  </Footer>
</template>
<script setup lang="ts">
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import {
    deleteDocumentGroup,
    getDocumentGroupsByPayrollConfigId,
  } from '../services/DocumentService'
  import router from '@/router'
  import Footer from '@/shared/components/Footer.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import Toolset from '@/shared/components/Toolset.vue'
  import { useConfirm } from 'primevue/useconfirm'
  import useNotifications from '@/store/notification'
  import DocumentGroup from '../models/DocumentGroup'
  import useLoading from '@/store/load'

  const payrollConfigId = ref()
  const toolset = ref()
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const pagination = reactive({
    currentPage: 0,
    lastPage: 0,
    limit: 10,
  })
  const loadingStore = useLoading()

  const documentGroups = reactive({
    data: [] as DocumentGroup[],
    totalItems: 0,
  })

  const path = useRoute()

  onMounted(async () => {
    await refreshData()
  })

  const toAdd = () => {
    router.push(
      '/payrollsConfig/' + payrollConfigId.value + '/documentGroups/add'
    )
  }

  const searcher = async () => {
    const { id } = path.params
    const response = await getDocumentGroupsByPayrollConfigId(
      String(id),
      toolset.value.search
    )
    documentGroups.data = response.data
    documentGroups.totalItems = response.totalItems
  }

  const refreshData = async () => {
    loadingStore.setLoadingState()
    const { id } = path.params
    payrollConfigId.value = id
    const response = await getDocumentGroupsByPayrollConfigId(String(id))
    documentGroups.data = response.data
    documentGroups.totalItems = response.totalItems
    loadingStore.dismissLoadingState()
  }

  const goToDetails = async (id: string) => {
    const { id: payrollConfigId } = path.params
    router.push(
      '/payrollsConfig/' + payrollConfigId + '/documentGroups/detail/' + id
    )
  }

  const deleteApprovalFlow = async (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar este registro?',
      acceptClass: 'p-button-danger',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const response = await deleteDocumentGroup(id)
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Grupo de documentos eliminado con exito.`,
          })
          await refreshData()
        }
      },
      reject: () => {},
    })
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${documentGroups.totalItems}`
  })
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
